//Components
import Module from '../../../../layout/Module'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import SelectField from '../../../../FormControl/input/SelectField'
import SurveyTemplateDialog from '../../components/SurveyTemplateDialog/SurveyTemplateDialog'
import Button from '@mui/material/Button'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import InputDateUTC from '../../../../FormControl/input/InputDateUTC'
import CircularProgress from '@mui/material/CircularProgress'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import { Skeleton, Typography } from '@mui/material'

//Utils
import cleanSurvey from '../../helpers/surveyCleaner'
import {
  getSurvey,
  updateSurveyRecord,
  createSurvey,
  getSurveyNameTaken
} from '../../../../../services/data-entry/surveys.service'
import { debounce } from '../../../../../utils/debounce'

//Other dependencies
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useState, useEffect, useCallback } from 'react'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { useRef } from 'react'
import { toggleAlertModule, alertReset } from '../../../../../store/actions/alert.actions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate, useParams } from 'react-router-dom'
import { STATUSDRAFT, STATUSACTIVE } from '../../constants/common'
import {
  checkedUserIds,
  updateSurveysBuilderTemplate,
  updateSurveyBuilder,
  resetSurveyBuilder,
  loadClassForSurvey
} from '../../../../../store/actions/dataEntrySurveyBuilderActions'
import { loadUsersForSurvey } from '../../../../../store/actions/dataEntrySurveyBuilderActions'
import { updateFilter } from '../../../../../store/actions/filter.actions'
import { updateSkip } from '../../../../../store/actions/datatable.actions'
import useFetchData from '../../../../../hooks/useFetchData'
import TableUser from './TableUser'
import TableClass from './TableClass'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import Constraints from '../Constraints/Constraints'
import AutocompleteFieldTemplateSurvey from '../../../../FormControl/AutocompleteFieldRequest/AutocompleteFieldTemplateSurvey'
import {
  SURVEY_TYPE_ABOUT_CLASSES,
  SURVEY_TYPE_ABOUT_TEACHER,
  SURVEY_TYPE_ABOUT_USERS,
  SURVEY_TYPE_STANDARD
} from '../../constants/typeSurvey'
// import './SurveyGlobal.css';
import isEmpty from 'lodash/isEmpty'

dayjs.extend(utc)
dayjs.extend(timezone)

const statuses = [
  { id: 'draft', label: 'Draft', value: 'draft' },
  { id: 'active', label: 'Active', value: 'active' }
]

const SurveyGlobal = ({ mode = 'adding' }) => {
  const currentModule = 'dataentrysurveys'
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const saveRef = useRef(null)
  const { surveyId, ...params } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const [list, setList] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [filteredRoles, setFilteredRoles] = useState([])
  const [showPreviewDialog, setShowPreviewDialog] = useState(false)
  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const [surveyNameTaken, setSurveyNameTaken] = useState('')
  const [type, setType] = useState(params.type || SURVEY_TYPE_STANDARD)
  const [included, setIncluded] = useState('all') // all, included, not-included

  const permissions = useSelector((state) => state?.session?.permissions?.dataentrysurveys ?? {})

  const skip = useSelector((state) =>
    type === SURVEY_TYPE_ABOUT_CLASSES
      ? (state?.datatable?.surveyClassList?.skip ?? 0)
      : (state?.datatable?.surveyUsersList?.skip ?? 0)
  )
  const rowsPerPage = useSelector((state) =>
    type === SURVEY_TYPE_ABOUT_CLASSES
      ? (state?.datatable?.surveyClassList?.rowsPerPage ?? 100)
      : (state?.datatable?.surveyUsersList?.rowsPerPage ?? 100)
  )
  const builderUsersFilters = useSelector((state) =>
    type === SURVEY_TYPE_ABOUT_CLASSES
      ? state?.filter?.surveyClassList?.filter || {}
      : state?.filter?.builderUsers?.filter || {}
  )

  const alertSave = useSelector((state) => state?.alert?.save || false)
  const module = useSelector((state) => state.dataEntrySurveyBuilder || {})
  const timezone = useSelector((state) => state.tenant?.timezone || 'Australia/Victoria')
  const surveysBuilder = module?.surveysBuilder ?? {}
  const { dueDate, name, error, template, checkedIds, status, constraints } = surveysBuilder
  const loading = surveysBuilder?.loading
  const selectedTemplate = surveysBuilder?.template || null
  const isEditing = mode === 'editing'
  const surveyCode = isEditing ? surveyId : 'creating'
  const { isLoading: isLoadingRoles, data: dataRoles = [] } = useFetchData('roles/standard')
  const { isLoading: isLoadingYears, data: dataYears = [] } = useFetchData('setting/year')
  const shouldDisableElements = loading || isSaving || isLoadingRoles || isLoadingYears
  const foundStatus = statuses.find((item) => item.value === status)
  const isDraft = status === STATUSDRAFT

  const tempList = useSelector((state) =>
    type === SURVEY_TYPE_ABOUT_CLASSES
      ? (state.dataEntrySurveyBuilder?.surveysBuilder?.classesList ?? [])
      : (state.dataEntrySurveyBuilder?.surveysBuilder?.usersList ?? [])
  )

  // Check survey name taken debounced
  const debouncedCheckSurveyName = useRef(
    debounce(async (surveyName, id) => {
      try {
        const res = await getSurveyNameTaken(surveyName, id)

        if (res?.data?.found) {
          setSurveyNameTaken('This survey name is already taken')
        } else {
          setSurveyNameTaken('')
        }
      } catch (error) {
        setSurveyNameTaken('')
      }
    }, 300)
  )

  const onAlert = (show, value, onSave, onDontSave) => {
    dispatch(toggleAlertModule(show, 'surveys', value, onSave, onDontSave))
  }

  const onChangeAlert = () => {
    if (alertSave && !alertSave.mode) {
      onAlert(false, mode, () => {
        if (saveRef?.current) {
          saveRef.current.click()
        }
      })
    }
  }

  const onLoadTableData = (skip, rowsPerPage, newFilters, surveyId) => {
    const filter = generateObjectFilter(newFilters, selectedTemplate?.roles ?? [])

    if (type === SURVEY_TYPE_ABOUT_CLASSES) {
      dispatch(loadClassForSurvey(skip, rowsPerPage, filter, surveyId))
    } else {
      dispatch(loadUsersForSurvey(skip, rowsPerPage, filter, surveyId))
    }
  }

  const generateObjectFilter = (builderUsersFilters, rolesTemplate = []) => {
    let roles = [...rolesTemplate]

    if (type === SURVEY_TYPE_ABOUT_TEACHER) {
      roles = dataRoles?.filter((item) => item.id === 'Teacher')
    } else if (type === SURVEY_TYPE_STANDARD) {
      roles = builderUsersFilters?.roles?.length ? builderUsersFilters?.roles : roles
    }

    const rolesId = roles?.map((item) => item?.id)
    const newFilters = {
      ...builderUsersFilters,
      roles: roles,
      rolesId
    }

    return newFilters
  }

  useEffect(() => {
    if (type === SURVEY_TYPE_ABOUT_TEACHER) {
      setFilteredRoles(['Teacher'])
    }
  }, [])

  useEffect(() => {
    const fetchSurveyData = async () => {
      try {
        const { data } = await getSurvey(surveyId)

        if (data) {
          const {
            name: dataName = '',
            dueDate: dataDueDate = null,
            template: dataTemplate = null,
            users: dataUsers = [],
            data: customData = {},
            status
          } = data

          let newIds = []
          const type = data?.type || params.type || SURVEY_TYPE_STANDARD
          if (type === SURVEY_TYPE_STANDARD) {
            newIds = dataUsers?.map((item) => String(item._id))
          } else if (type === SURVEY_TYPE_ABOUT_CLASSES) {
            newIds = customData?.data?.map((item) => String(item._id))
          } else if (SURVEY_TYPE_ABOUT_USERS.includes(type)) {
            newIds = customData?.data?.map((item) => String(item._id))
          }
          setType(type)
          onChangeTemplate({
            ...dataTemplate,
            value: dataTemplate?._id || dataTemplate?.value,
            label: dataTemplate?.title || dataTemplate?.label
          })
          dispatch(checkedUserIds(newIds))
          dispatch(updateSurveyBuilder({ name: dataName }))
          dispatch(updateSurveyBuilder({ updateUsers: dataUsers }))
          dispatch(updateSurveyBuilder({ dueDate: new Date(dataDueDate) }))
          dispatch(updateSurveyBuilder({ status: status }))
          dispatch(updateSurveyBuilder({ constraints: customData.constraints }))
        }
      } catch (error) {
        console.error('Error loading survey data:', error)
        enqueueSnackbar('Failed to load survey data. Please try again.', { variant: 'error' })
      }
    }

    if (surveyId) {
      fetchSurveyData()
    }
  }, [surveyId])

  const onLoadData = useCallback(() => {
    if (selectedTemplate) {
      const templateRoles = selectedTemplate?.roles?.map(({ id }) => id) || []
      const rolesData = dataRoles?.map(({ id }) => id) || []

      setFilteredRoles(templateRoles?.length > 0 ? templateRoles : (rolesData ?? []))

      const newFilters = structuredClone(builderUsersFilters)

      // Apply inclusion filter
      if (included === 'included') {
        newFilters.show = 'included'
        newFilters.includedId = checkedIds
      } else if (included === 'not-included') {
        newFilters.show = 'not-included'
        newFilters.excludedId = checkedIds
      }

      onLoadTableData(skip, rowsPerPage, newFilters, surveyCode)
    }
  }, [skip, rowsPerPage, selectedTemplate, builderUsersFilters, surveyCode, included])

  const onChangeTemplate = (template = {}) => {
    const { roles = [] } = template || {}
    const rolesId = roles?.map(({ id }) => id)
    const moduleFilter = type === SURVEY_TYPE_ABOUT_CLASSES ? 'surveyClassList' : 'builderUsers'
    const moduleSkip = type === SURVEY_TYPE_ABOUT_CLASSES ? 'surveyClassList' : 'surveyUsersList'
    let newFilters = generateObjectFilter(builderUsersFilters, template?.roles ?? [])
    dispatch(updateSkip(moduleSkip, 0))
    dispatch(updateSkip(moduleFilter, 0))
    dispatch(updateFilter(moduleFilter, newFilters))
    dispatch(updateSurveysBuilderTemplate(template))
    let constraints = {
      rolesId,
      classRelated: false
    }
    if (SURVEY_TYPE_ABOUT_CLASSES === type) {
      constraints = {
        rolesId: ['Student'],
        classRelated: true
      }
    } else if (SURVEY_TYPE_ABOUT_TEACHER === type) {
      constraints = {
        rolesId: [],
        classRelated: true
      }
    }

    dispatch(updateSurveyBuilder({ 'constraints': constraints }))
  }

  const onChangeStatus = (value) => {
    if (value) {
      dispatch(updateSurveyBuilder({ 'status': value }))
      onChangeAlert()
    }
  }

  const onCancel = () => {
    dispatch(resetSurveyBuilder())
    dispatch(updateFilter('builderUsers', {}))
    dispatch(updateFilter('surveyClassList', {}))
    dispatch(updateSkip('builderUsers', 0))
    dispatch(updateSkip('surveyClassList', 0))
    dispatch(checkedUserIds([]))
    navigate('/dashboard/data-entry/surveys')
  }

  const validateForm = () => {
    let error = {}
    dispatch(updateSurveyBuilder({ error: {} }))

    if (!template) {
      error.template = 'Please choose a template'
    }

    if (!name || !name.trim().length) {
      error.name = 'Please enter survey name'
    }

    if (!status || !status.trim().length) {
      error.status = 'Please choose status'
    }

    if (!dueDate) {
      error.dueDate = 'Please enter survey due date'
    }
    if (type === SURVEY_TYPE_ABOUT_TEACHER && isEmpty(constraints?.rolesId)) {
      error.constraintsRolesId = 'Please choose preference'
    }

    if (!isEmpty(error)) {
      dispatch(
        updateSurveyBuilder({
          error: error
        })
      )
      return false
    }

    if (status === STATUSACTIVE && (!checkedIds || !Array.isArray(checkedIds) || !checkedIds.length)) {
      enqueueSnackbar(
        `Please select users to ${mode === 'editing' ? 'update this survey' : 'create a survey'}`,
        {
          variant: 'error'
        }
      )

      return false
    }

    return true
  }

  const getPayload = () => {
    const payload = cleanSurvey(surveysBuilder)

    if (mode === 'editing') {
      delete payload.notes
      delete payload.round
    }

    return payload
  }

  const updateSurveyData = async () => {
    setShowPublishDialog(false)
    setIsSaving(true)

    const payload = getPayload()
    const { data, error } = await updateSurveyRecord(surveyId, { ...payload })

    setIsSaving(false)

    if (error || !data) {
      enqueueSnackbar(`We could not ${isDraft ? 'save' : 'publish'} changes`, {
        variant: 'error'
      })
    } else {
      dispatch(alertReset())

      if (!isDraft) {
        dispatch(resetSurveyBuilder())
        navigate('/dashboard/data-entry/surveys')
      } else {
        enqueueSnackbar('Survey updated successfully', {
          variant: 'success'
        })
      }
      return
    }
  }

  const createSurveyData = async () => {
    setShowPublishDialog(false)
    setIsSaving(true)

    const payload = getPayload()
    const { data, error } = await createSurvey({ ...payload, type })

    if (error || !data) {
      enqueueSnackbar('We could not create the survey', {
        variant: 'error'
      })
    } else {
      dispatch(resetSurveyBuilder())
      dispatch(alertReset())
      onCancel()
      return
    }

    setIsSaving(false)
  }

  const handleOnSaveSurvey = async () => {
    try {
      const isFormValid = validateForm()

      if (isFormValid) {
        const { data: surveyNameCheck } = await getSurveyNameTaken(name, isEditing ? surveyId : '')

        if (surveyNameCheck?.found) {
          setSurveyNameTaken('This survey name is already taken')
          return
        } else {
          setSurveyNameTaken('')
          if (mode === 'editing') {
            if (!isDraft) {
              setShowPublishDialog(true)
            } else {
              await updateSurveyData()
            }
          } else {
            if (!isDraft) {
              setShowPublishDialog(true)
            } else {
              await createSurveyData()
            }
          }
        }
      }
    } catch (error) {
      setShowPublishDialog(false)
      setIsSaving(false)
      enqueueSnackbar('We could not perform this action, please try again', {
        variant: 'error'
      })
    }
  }

  const onChangeHeader = (name, value) => {
    dispatch(updateSurveyBuilder({ [name]: value, error: { ...error, [name]: null } }))

    if (name === 'name' && value && value.trim().length) {
      debouncedCheckSurveyName.current(value, isEditing ? surveyId : '')
    }
    onChangeAlert()
  }

  useEffect(() => {
    onLoadData()
  }, [onLoadData])

  const onLoad = useCallback(() => {
    setList(tempList)
  }, [tempList])

  useEffect(() => {
    onLoad()
  }, [onLoad])

  useEffect(() => {
    if (included) {
      //when included changes reset skip
      const moduleSkip = type === SURVEY_TYPE_ABOUT_CLASSES ? 'surveyClassList' : 'surveyUsersList'
      dispatch(updateSkip(moduleSkip, 0))
    }
  }, [included])

  return (
    <Module>
      <Module.Header
        moduleName={currentModule}
        title='Survey Builder'
        titleLink={'/dashboard/data-entry/surveys/add'}
      />
      <Module.BodyHeader py='6' className='border-b border-b-2 border-primary relative '>
        <Box className='flex w-full items-center gap-2'>
          <Box
            style={{ width: '10%' }}
            className='flex items-center justify-center gap-2 min-w-120px max-w-120px'
          >
            {loading ? (
              <Typography className='flex flex-col gap-2 items-end   justify-center'>
                <Skeleton width={'100px'} />
                <Skeleton width={'50px'} />
              </Typography>
            ) : type === SURVEY_TYPE_STANDARD ? (
              <Typography color={'primary'} className='text-center'>
                Standard survey
              </Typography>
            ) : (
              <Typography color={'primary'} className='text-right'>
                Survey {type?.split('-')?.join(' ')}
              </Typography>
            )}
          </Box>
          <Box style={{ width: '25%' }} className='flex items-center gap-2'>
            <AutocompleteFieldTemplateSurvey
              id='generate-assessment-template'
              disabled={mode === 'editing' || shouldDisableElements}
              name='template'
              placeholder='Template*'
              value={selectedTemplate}
              error={error?.template ? true : false}
              helperText={error?.template}
              onChange={(value) => onChangeTemplate(value)}
              label={'Template*'}
              variant='standard'
              filter={type === SURVEY_TYPE_ABOUT_TEACHER ? { rolesName: ['Teacher'] } : {}}
              isPreview={isEditing}
            />
            {permissions?.list && (
              <IconTooltipButton
                title={'Preview template'}
                disabled={shouldDisableElements || !selectedTemplate}
                icon={'expand-alt'}
                className={'survey__preview-button'}
                onClick={() => setShowPreviewDialog(true)}
              />
            )}
          </Box>
          <Box style={{ width: '180px' }}>
            <TextField
              id='generate-survey-name'
              name='name'
              value={name || ''}
              error={error?.name || surveyNameTaken ? true : false}
              helperText={error?.name || surveyNameTaken}
              label='Name*'
              onChange={(e) => onChangeHeader('name', e.target.value)}
              InputProps={{ required: true }}
              style={{ mr: 1, width: '100%' }}
              variant='standard'
              disabled={shouldDisableElements}
            />
          </Box>
          <Box style={{ width: '180px' }}>
            <InputDateUTC
              className='custom-text-field survey__custom-date-picker'
              id='svy-due-date'
              variant='standard'
              autoOk={true}
              name='date'
              value={dueDate ? new Date(dueDate) : null}
              error={error?.dueDate ? true : false}
              helperText={error?.dueDate}
              fullWidth
              label={'Due date*'}
              onChange={(value) => onChangeHeader('dueDate', value)}
              isStartOfTheDay={false}
              minDate={dayjs(new Date()).tz(timezone).toDate()}
              disabled={shouldDisableElements}
            />
          </Box>
          <Box style={{ width: '180px' }}>
            <SelectField
              id='survey-status'
              disabled={shouldDisableElements}
              name='status'
              placeholder='Status'
              value={foundStatus?.value || ''}
              error={error?.status ? true : false}
              helperText={error?.status}
              options={statuses ?? []}
              onChange={(e) => onChangeStatus(e.target.value)}
              label={'Status*'}
              variant='standard'
            />
          </Box>
          <Box style={{ width: '180px' }}>
            {[...SURVEY_TYPE_ABOUT_USERS].includes(type) && (
              <Constraints
                filteredRolesId={filteredRoles ?? []}
                disabled={shouldDisableElements || !selectedTemplate}
                dataRoles={dataRoles ?? []}
                isLoadingRoles={isLoadingRoles}
                value={constraints?.rolesId?.[0] ?? ''}
                error={error?.constraintsRolesId}
              />
            )}
          </Box>
        </Box>
        <Box className='flex items-center gap-2 justify-end'>
          <AlertSaveButton
            submodule='Surveys'
            disabled={loading}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm'
            onDontSave={onCancel}
          />
          {permissions.create && (
            <Box sx={{ display: 'flex', position: 'relative' }}>
              {isSaving && (
                <CircularProgress
                  size={25}
                  style={{ position: 'absolute', left: '35%', top: '2px', zIndex: 1 }}
                />
              )}
              <Button
                size='small'
                onClick={handleOnSaveSurvey}
                disabled={shouldDisableElements}
                startIcon={
                  <FontAwesomeIcon icon={status === STATUSACTIVE ? 'paper-plane' : 'circle-check'} />
                }
                style={{
                  // padding: '6px 16px',
                  color: shouldDisableElements ? 'rgba(0, 0, 0, 0.26)' : '#fff',
                  backgroundColor: shouldDisableElements ? 'rgba(0, 0, 0, 0.12)' : '#32668b'
                }}
                // className={'custom-save__button'}
                ref={saveRef}
              >
                {status === STATUSACTIVE ? 'Publish' : 'Save'}
              </Button>
            </Box>
          )}
        </Box>
      </Module.BodyHeader>
      <Module.BodyContainer>
        {type === SURVEY_TYPE_ABOUT_CLASSES ? (
          <TableClass
            filteredRoles={filteredRoles}
            isSaving={isSaving}
            list={list}
            isLoadingYears={isLoadingYears}
            dataYears={dataYears}
            dataRoles={dataRoles}
            included={included}
            setIncluded={setIncluded}
          />
        ) : (
          <TableUser
            filteredRoles={filteredRoles}
            isSaving={isSaving}
            list={list}
            isLoadingYears={isLoadingYears}
            isLoadingRoles={isLoadingRoles}
            dataYears={dataYears}
            dataRoles={dataRoles}
            type={type}
            included={included}
            setIncluded={setIncluded}
          />
        )}
      </Module.BodyContainer>
      <SurveyTemplateDialog
        id={`template-survey-list-modal`}
        open={showPreviewDialog}
        onCancel={() => setShowPreviewDialog(false)}
        onConfirm={() => setShowPreviewDialog(false)}
        loading={loading}
        title={selectedTemplate?.title || 'Template Preview'}
        onCloseHeader={true}
        maxWidth='lg'
        showActions={false}
        questions={selectedTemplate?.questions?.map((item) => {
          return {
            ...item,
            answer: [],
            error: null
          }
        })}
      />
      <DialogConfirmCancel
        id={`publish-survey-modal`}
        open={showPublishDialog}
        onCancel={() => setShowPublishDialog(false)}
        onConfirm={() => {
          isEditing ? updateSurveyData() : createSurveyData()
        }}
        loading={loading}
        title={'Publish Survey'}
        onCloseHeader={true}
        maxWidth='xs'
      >
        <Typography component={'div'}>Are you sure you want to publish this survey ?</Typography>
      </DialogConfirmCancel>
    </Module>
  )
}

export default SurveyGlobal
